import { CheckedAddress, NormalizedNumber } from '@sparkdotfi/common-universal'

import { Token } from '@/domain/types/Token'
import { SuccessViewCheckmark } from '@/features/dialogs/common/components/success-view/SuccessViewCheckmark'
import { SuccessViewContent } from '@/features/dialogs/common/components/success-view/SuccessViewContent'
import { SuccessViewProceedButton } from '@/features/dialogs/common/components/success-view/SuccessViewProceedButton'
import { HorizontalScroll } from '@/ui/atoms/horizontal-scroll/HorizontalScroll'
import { BlockExplorerAddressLink } from '@/ui/molecules/block-explorer-address-link/BlockExplorerAddressLink'
import { TransactionOverview } from '@/ui/organisms/transaction-overview/TransactionOverview'
import { testIds } from '@/ui/utils/testIds'

export interface SuccessViewProps {
  staked?: NormalizedNumber
  spk: Token
  delegatedTo:
    | {
        address: CheckedAddress
        name: string
        avatar?: string
      }
    | undefined
  proceedAction: () => void
  proceedLabel: string
}
export function SuccessView({ staked, spk, delegatedTo, proceedAction, proceedLabel }: SuccessViewProps) {
  return (
    <SuccessViewContent>
      <SuccessViewCheckmark />
      <div
        className="mt-8 grid w-full grid-cols-[auto_1fr] gap-x-6 rounded-sm bg-secondary"
        style={{ gridAutoRows: 'minmax(0, 1fr)' }}
        data-testid={testIds.dialog.success}
      >
        {staked && (
          <TransactionOverview.Row>
            <TransactionOverview.Label>Staked</TransactionOverview.Label>
            <TransactionOverview.TokenAmountOutcome
              token={spk}
              amount={staked}
              showUsdValue
              amountDataTestId={testIds.dialog.delegate.success.transactionOverview.staked}
            />
          </TransactionOverview.Row>
        )}
        <TransactionOverview.Row>
          <TransactionOverview.Label>{delegatedTo ? 'Delegating to' : 'Delegation outcome'}</TransactionOverview.Label>
          {delegatedTo ? (
            <HorizontalScroll>
              <div className="flex w-fit items-center gap-1.5">
                {delegatedTo.avatar && <img src={delegatedTo.avatar} alt={delegatedTo.name} className="size-4" />}
                <div className="flex items-baseline gap-1">
                  <div
                    className="typography-label-2 text-primary"
                    data-testid={testIds.dialog.delegate.success.transactionOverview.delegateTo.name}
                  >
                    {delegatedTo.name}
                  </div>
                  <BlockExplorerAddressLink
                    address={delegatedTo.address}
                    className="typography-body-4 text-secondary"
                    compact
                    data-testid={testIds.dialog.delegate.success.transactionOverview.delegateTo.address}
                  />
                </div>
              </div>
            </HorizontalScroll>
          ) : (
            <div
              className="typography-label-2 text-primary"
              data-testid={testIds.dialog.delegate.success.transactionOverview.noDelegationText}
            >
              No delegation
            </div>
          )}
        </TransactionOverview.Row>
      </div>
      <SuccessViewProceedButton onProceed={proceedAction} dataTestId={testIds.dialog.delegate.success.proceedButton}>
        {proceedLabel}
      </SuccessViewProceedButton>
    </SuccessViewContent>
  )
}
